import {
    ButtonGradientPrimary,
    Colors,
    Container,
    DraggableList,
    FOCUS_RING,
    Icon,
    Pills,
    Text,
    type DraggableItem,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useState } from 'react';

import { AgendaDiscussionPointRow } from './AgendaDiscussionPointRow';
import { TopicEditForm } from './TopicEditForm';
import { useAgendaEditorContext } from '../AgendaEditorContext';

import type { AgendaItemView, AgendaPointView } from '../agendaViewModel';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import type { ReactNode } from 'react';

import { GripVerticalIcon } from '@/components/GripVerticalIcon/GripVerticalIcon';
import { useAmelioTranslations } from '@/hooks';

interface AgendaItemRowProps {
    item: AgendaItemView;
    /** Extra controls rendered in the header, beside the expand toggle — e.g. the thematic
     * meeting's delete-topic button. Kept on this row (rather than a wrapper around it) so the
     * drag handle stays the direct child `DraggableList`/`SortableRow` clone their drag props onto. */
    actions?: ReactNode;
    dragListeners?: SyntheticListenerMap;
    dragAttributes?: DraggableAttributes;
}

export const AgendaItemRow = ({
    item,
    actions,
    dragListeners,
    dragAttributes,
}: AgendaItemRowProps) => {
    const { t } = useAmelioTranslations();
    const {
        editor: { topics, points },
        disabled,
        isRecord,
    } = useAgendaEditorContext();
    const [expandedPointId, setExpandedPointId] = useState<string | null>(null);
    const [isEditingTopic, setIsEditingTopic] = useState(false);
    const isExpanded = topics.isExpanded(item.id);
    const onToggleExpand = () => topics.toggleExpanded(item.id);

    const draggablePoints: DraggableItem<AgendaPointView>[] = item.discussionPoints.map(
        (point) => ({ id: point.id, data: point }),
    );

    return (
        <div
            // Two exclusive paddings rather than a base class and its `md:` variant, which the base
            // would win (see src/responsive-utilities.css).
            className={
                'border-base-100 backdrop-blur-glass-md w-full overflow-hidden rounded-xl border bg-white/70 max-md:p-3 md:p-6'
            }
        >
            <div className={'group flex w-full items-center justify-between gap-3'}>
                <div className={'flex flex-1 items-center gap-4'}>
                    {/* No handle on a read-only agenda: the list refuses the reorder, so a grip
                        would promise a gesture that does nothing. */}
                    {disabled ? null : (
                        <div
                            className={`hidden! cursor-move md:block! ${FOCUS_RING}`}
                            data-testid={`agenda-item-drag-handle-${item.id}`}
                            {...dragListeners}
                            {...dragAttributes}
                            aria-label={t('meetingAgenda.reorderTopic')}
                        >
                            <GripVerticalIcon
                                className={
                                    'text-base-300 group-hover:text-primary h-[24px] w-[24px]'
                                }
                            />
                        </div>
                    )}
                    {isEditingTopic ? (
                        <TopicEditForm
                            initialTitle={item.title}
                            initialDurationMinutes={item.durationMinutes}
                            onConfirm={(title, durationMinutes) => {
                                topics.update({ itemId: item.id, title, durationMinutes });
                                setIsEditingTopic(false);
                            }}
                            onCancel={() => setIsEditingTopic(false)}
                            dataTestId={`edit-topic-form-${item.id}`}
                        />
                    ) : (
                        <>
                            <div className={'flex flex-1 items-center gap-3'}>
                                <button
                                    type={'button'}
                                    className={
                                        'flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-3 border-0 bg-transparent p-0 text-left md:flex-row md:items-center'
                                    }
                                    onClick={onToggleExpand}
                                    aria-expanded={isExpanded}
                                    data-testid={`agenda-item-label-${item.id}`}
                                >
                                    <Text
                                        as={'span'}
                                        weight={'semibold'}
                                        color={Colors.BASE_600}
                                        className={
                                            'truncate overflow-visible text-[16px] leading-[24px] text-clip whitespace-normal'
                                        }
                                    >
                                        {item.title}
                                    </Text>

                                    {/* The design's 12px duration on the md pill's padding. The
                                        pill concatenates its classes, so the size needs the `!`. */}
                                    <Pills
                                        variant={'primary'}
                                        size={'md'}
                                        className={'w-fit !text-[12px]'}
                                    >
                                        {t('meetingAgenda.minutes', {
                                            count: item.durationMinutes,
                                        })}
                                    </Pills>
                                </button>

                                {!isRecord && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type={'button'}
                                                    onClick={() => setIsEditingTopic(true)}
                                                    disabled={disabled}
                                                    // `p-1` alone left a 25 x 28 px target, calibrated on the
                                                    // glyph rather than on the hand: 24 px is the floor, and
                                                    // a floor is not a target.
                                                    className={
                                                        'hover:bg-primary/10 inline-flex h-8 w-8 items-center justify-center rounded transition-opacity group-hover:opacity-100 disabled:opacity-40 md:opacity-0'
                                                    }
                                                    aria-label={t('meetingAgenda.editAgendaItem')}
                                                    data-testid={`edit-agenda-item-button-${item.id}`}
                                                >
                                                    <Icon
                                                        name={'edit-edit-2'}
                                                        size={'sm'}
                                                        color={'inherit'}
                                                        className={
                                                            'text-base-400 hover:text-primary transition-colors'
                                                        }
                                                    />
                                                </button>
                                            </TooltipTrigger>

                                            <TooltipContent side={'top'}>
                                                <p>{t('meetingAgenda.editAgendaItem')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}

                                {item.hasHandout && (
                                    <ButtonGradientPrimary
                                        size={'sm'}
                                        fitContent
                                        leftIcon={
                                            <Icon
                                                name={'edit-document-text'}
                                                size={'xs'}
                                                color={Colors.WHITE}
                                            />
                                        }
                                        dataTestId={`open-handout-button-${item.id}`}
                                        className={'!h-10 !w-fit !text-base hover:!scale-100'}
                                    >
                                        {t('meetingAgenda.openHandout')}
                                    </ButtonGradientPrimary>
                                )}
                            </div>

                            <button
                                type={'button'}
                                // Same target size as the delete button (`h-9 w-9`) on this row: the
                                // icon was already the trash's size (`sm`), but a tighter box gave it
                                // less breathing room and read as bigger.
                                className={
                                    'inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center'
                                }
                                onClick={onToggleExpand}
                                aria-expanded={isExpanded}
                                // `aria-expanded` carries the state, so the name stays neutral.
                                aria-label={t('meetingAgenda.toggleAgendaItem')}
                                data-testid={`toggle-agenda-item-${item.id}`}
                            >
                                <Icon
                                    name={'arrow-arrow-down'}
                                    size={20}
                                    color={Colors.BASE_400}
                                    rotate={isExpanded ? 180 : 0}
                                />
                            </button>
                        </>
                    )}
                </div>

                {actions}
            </div>

            {isExpanded && (
                // The panel opens right under the rule that separates it from the topic: the card's
                // own padding already sets it in from the edges.
                <div className={'border-base-100 mt-2 space-y-4 border-t pt-4'}>
                    <>
                        <Container
                            p={item.discussionPoints.length > 0 ? 'md' : 'sm'}
                            borderRadius={'lg'}
                            className={'bg-white/60'}
                            dataTestId={`agenda-item-${item.id}-discussion-points-panel`}
                        >
                            {item.discussionPoints.length === 0 ? (
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'text-center'}
                                >
                                    {t(
                                        isRecord
                                            ? 'meetingAgenda.readOnly.noDiscussionPoints'
                                            : 'meetingAgenda.noDiscussionPoints',
                                    )}
                                </Text>
                            ) : (
                                <DraggableList
                                    items={draggablePoints}
                                    onReorder={(reordered) =>
                                        points.reorder(
                                            item.id,
                                            reordered.map((entry) => entry.data),
                                        )
                                    }
                                    gap={24}
                                    dataTestId={`agenda-item-${item.id}-discussion-points`}
                                    useDragHandle
                                    renderItem={(draggableItem, metadata) => (
                                        // The row takes the drag props itself, like a topic row: its grip
                                        // then sits beside the question box and centers on it.
                                        <AgendaDiscussionPointRow
                                            itemId={item.id}
                                            point={draggableItem.data}
                                            isDragging={metadata.isDragging}
                                            isResponseExpanded={
                                                expandedPointId === draggableItem.data.id
                                            }
                                            onToggleResponse={() =>
                                                setExpandedPointId((current) =>
                                                    current === draggableItem.data.id
                                                        ? null
                                                        : draggableItem.data.id,
                                                )
                                            }
                                        />
                                    )}
                                />
                            )}
                        </Container>

                        {!isRecord && (
                            <ButtonGradientPrimary
                                onClick={() => points.add(item.id)}
                                disabled={disabled || !points.canAddTo(item.id)}
                                size={'sm'}
                                fitContent
                                leftIcon={
                                    <Icon
                                        name={'essential-add'}
                                        color={Colors.WHITE}
                                        className={'!h-5 !w-5'}
                                    />
                                }
                                dataTestId={`add-discussion-point-button-${item.id}`}
                                className={'mt-4 !h-9 hover:!scale-100'}
                            >
                                {t('meetingAgenda.addDiscussionPoint')}
                            </ButtonGradientPrimary>
                        )}
                    </>
                </div>
            )}
        </div>
    );
};

export default AgendaItemRow;
